import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserEditModal = ({ user, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    status: '',
    permissions: {
      canCreateContent: false,
      canReviewContent: false,
      canApproveContent: false,
      canManageUsers: false,
      canViewReports: false,
      canConfigureRules: false
    }
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        role: user?.role || '',
        department: user?.department || '',
        status: user?.status || '',
        permissions: user?.permissions || {
          canCreateContent: false,
          canReviewContent: false,
          canApproveContent: false,
          canManageUsers: false,
          canViewReports: false,
          canConfigureRules: false
        }
      });
    }
  }, [user]);

  const departmentOptions = [
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Compliance', label: 'Compliance' },
    { value: 'Legal', label: 'Legal' },
    { value: 'Operations', label: 'Operations' }
  ];

  const roleOptions = [
    { value: 'Marketing Creator', label: 'Marketing Creator' },
    { value: 'Compliance Officer', label: 'Compliance Officer' },
    { value: 'Marketing Director', label: 'Marketing Director' },
    { value: 'Legal Reviewer', label: 'Legal Reviewer' }
  ];

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Pending', label: 'Pending' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePermissionChange = (permission, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev?.permissions,
        [permission]: checked
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData?.role) {
      newErrors.role = 'Role is required';
    }
    
    if (!formData?.department) {
      newErrors.department = 'Department is required';
    }
    
    if (!formData?.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({
        ...user,
        ...formData
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {user?.id ? 'Edit User' : 'Add New User'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            iconSize={20}
            onClick={onClose}
          >
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter full name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="Enter email address"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Department"
              placeholder="Select department"
              options={departmentOptions}
              value={formData?.department}
              onChange={(value) => handleInputChange('department', value)}
              error={errors?.department}
              required
            />

            <Select
              label="Role"
              placeholder="Select role"
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => handleInputChange('role', value)}
              error={errors?.role}
              required
            />

            <Select
              label="Status"
              placeholder="Select status"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleInputChange('status', value)}
              error={errors?.status}
              required
            />
          </div>

          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Permissions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Checkbox
                label="Create Content"
                description="Can create and draft marketing content"
                checked={formData?.permissions?.canCreateContent}
                onChange={(e) => handlePermissionChange('canCreateContent', e?.target?.checked)}
              />

              <Checkbox
                label="Review Content"
                description="Can review and provide feedback on content"
                checked={formData?.permissions?.canReviewContent}
                onChange={(e) => handlePermissionChange('canReviewContent', e?.target?.checked)}
              />

              <Checkbox
                label="Approve Content"
                description="Can approve content for publication"
                checked={formData?.permissions?.canApproveContent}
                onChange={(e) => handlePermissionChange('canApproveContent', e?.target?.checked)}
              />

              <Checkbox
                label="Manage Users"
                description="Can manage team members and permissions"
                checked={formData?.permissions?.canManageUsers}
                onChange={(e) => handlePermissionChange('canManageUsers', e?.target?.checked)}
              />

              <Checkbox
                label="View Reports"
                description="Can access compliance reports and analytics"
                checked={formData?.permissions?.canViewReports}
                onChange={(e) => handlePermissionChange('canViewReports', e?.target?.checked)}
              />

              <Checkbox
                label="Configure Rules"
                description="Can modify regulatory rules and settings"
                checked={formData?.permissions?.canConfigureRules}
                onChange={(e) => handlePermissionChange('canConfigureRules', e?.target?.checked)}
              />
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;